import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useGlobalStore } from '../../store/global-store/global-store';
import {
  createSlashCommand,
  deleteFloraMemory,
  deleteSlashCommand,
  getDocumentContent,
  getDocumentPreview,
  getDocumentRevisions,
  getFloraCapabilities,
  getFloraMemories,
  getFloraUserPreferences,
  getOrganizationDocuments,
  getSlashCommands,
  getSuggestedPrompts,
  updateFloraUserPreferences,
  updateSlashCommand,
} from './agent-client';
import {
  type DocumentContentResponse,
  type DocumentRevision,
  type FloraUserPreferences,
  type FloraUserPreferencesResponse,
  type SlashCommand,
  type SlashCommandWrite,
  type UseFloraCapabilitiesResult,
  type UseFloraMemoriesResult,
  type UseFloraUserPreferencesResult,
  type UseDocumentPreviewResult,
  type UseOrganizationDocumentsResult,
  type UseSuggestedPromptsResult,
} from './agent-client.type';

/** How often (ms) to re-poll the preview endpoint while a conversion is running. */
const DOCUMENT_PREVIEW_POLL_MS = 2000;
/**
 * How long (ms) to keep polling a `processing` preview before giving up. The
 * backend surfaces `error` for deterministic conversion failures, but a
 * stalled/absent worker would keep returning `processing` forever — so after
 * this window we stop polling and treat it as failed.
 */
const MAX_DOCUMENT_PREVIEW_WAIT_MS = 90_000;

/**
 * Hook to get suggested prompts for the agent
 * @returns {UseSuggestedPromptsResult} Object containing suggested prompts data and query state
 */
const useSuggestedPrompts = (): UseSuggestedPromptsResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id) ?? null;
  const query = useQuery({
    queryKey: ['suggested-prompts', organizationId, portfolioId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getSuggestedPrompts(organizationId, portfolioId);
    },
    enabled: !!organizationId,
  });

  return {
    data: query.data,
    query,
  };
};

/** Query key prefix for the organization's documents list; invalidate it when documents change. */
const ORGANIZATION_DOCUMENTS_QUERY_KEY = 'organization-documents';

/**
 * Hook to fetch all documents for the current organization.
 *
 * The app disables refetch-on-mount globally, so a screen that must always
 * show the latest list (the Documents page) passes `refetchOnMount: 'always'`;
 * other callers keep the cached list.
 *
 * @param {{ refetchOnMount?: boolean | 'always' }} [options] - Overrides the global refetch-on-mount behavior for this observer
 * @returns {UseOrganizationDocumentsResult} Object containing documents, loading state, error, and refetch function
 */
const useOrganizationDocuments = (
  options: { refetchOnMount?: boolean | 'always' } = {},
): UseOrganizationDocumentsResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [ORGANIZATION_DOCUMENTS_QUERY_KEY, organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getOrganizationDocuments(organizationId);
    },
    enabled: !!organizationId,
    ...(options.refetchOnMount !== undefined ? { refetchOnMount: options.refetchOnMount } : {}),
  });

  return {
    documents: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Hook to fetch (and poll) the PDF preview for a Flora-generated Word/Excel
 * document. While the backend reports `processing`, the query re-polls every
 * `DOCUMENT_PREVIEW_POLL_MS`; it stops once the status is `ready` or `error`.
 * Caching is disabled so each mount fetches a fresh (presigned, expiring)
 * `preview_url`.
 *
 * @param {string | undefined} documentId - The document to preview; the query is disabled when absent
 * @param {number | undefined} revision - The document's current revision; a change refetches so an edited document's preview is not stale
 * @returns {UseDocumentPreviewResult} The preview payload, loading state, error, whether polling gave up, and a retry
 */
const useDocumentPreview = (documentId: string | undefined, revision?: number): UseDocumentPreviewResult => {
  const [timedOut, setTimedOut] = useState(false);
  // Bumped by `retry()` to force a brand-new query key, so a give-up can be
  // undone without waiting for the document or revision to change.
  const [attempt, setAttempt] = useState(0);
  // Reset the give-up flag when the target document OR its revision changes,
  // using React's render-phase "adjust state on prop change" pattern.
  const trackingKey = `${documentId ?? ''}:${revision ?? ''}`;
  const [trackedKey, setTrackedKey] = useState(trackingKey);
  if (trackingKey !== trackedKey) {
    setTrackedKey(trackingKey);
    setTimedOut(false);
  }

  const query = useQuery({
    // Revision is part of the key so an edit (which bumps it) refetches instead
    // of serving the previous revision's cached preview url.
    queryKey: ['document-preview', documentId, revision, attempt],
    queryFn: () => {
      if (!documentId) throw new Error('Document ID required');
      return getDocumentPreview(documentId, revision);
    },
    // Stop polling once we've given up, so a stalled worker can't poll forever.
    enabled: !!documentId && !timedOut,
    // Poll only while a conversion is in flight; settle on ready/error.
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? DOCUMENT_PREVIEW_POLL_MS : false),
    // Keep polling while the tab is backgrounded. TanStack gates interval
    // refetches on window focus by default (`queryObserver`: the interval only
    // fetches when `refetchIntervalInBackground` is set or the window is
    // focused), but the give-up timer below is plain wall-clock and keeps
    // running — so without this, switching tabs for 90s guarantees a spurious
    // "preview unavailable" for a conversion that was progressing fine.
    refetchIntervalInBackground: true,
    // The presigned preview_url expires, so never serve a cached one.
    staleTime: 0,
    gcTime: 0,
  });

  // Arm a single give-up timer while the conversion is processing; it clears if
  // the status settles (ready/error) or the document changes. `status` is a
  // stable string across polls, so this effect does not re-arm on each poll.
  const status = query.data?.status;
  useEffect(() => {
    if (status !== 'processing') return;
    const timer = setTimeout(() => setTimedOut(true), MAX_DOCUMENT_PREVIEW_WAIT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const retry = useCallback(() => {
    setTimedOut(false);
    setAttempt((previous) => previous + 1);
  }, []);

  return {
    preview: query.data,
    isLoading: query.isLoading,
    error: query.error,
    timedOut,
    retry,
  };
};

/**
 * Hook to fetch the text of a CSV, TSV, Markdown, JSON, or plain-text document
 * revision for in-app rendering. A revision's content never changes, so it is
 * cached per (document, revision); an edit bumps the revision and refetches.
 *
 * @param {string | undefined} documentId - The document to read; the query is disabled when absent
 * @param {number | undefined} revision - The revision to read; defaults to the current revision
 * @returns {{ content: DocumentContentResponse | undefined; isLoading: boolean; error: Error | null; refetch: () => void }} The content, loading state, error, and a retry
 */
const useDocumentContent = (
  documentId: string | undefined,
  revision?: number,
): {
  content: DocumentContentResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} => {
  const query = useQuery({
    queryKey: ['document-content', documentId, revision],
    queryFn: () => {
      if (!documentId) throw new Error('Document ID required');
      return getDocumentContent(documentId, revision);
    },
    enabled: !!documentId,
    retry: 1,
  });

  const { refetch } = query;
  const retryFetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    content: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: retryFetch,
  };
};

/**
 * Hook to fetch a document's revision history (newest first). Disabled until
 * `documentId` is provided so callers can gate it on an expanded/opened state.
 *
 * @param {string | undefined} documentId - The document whose revisions to list
 * @param {boolean} [enabled] - Extra gate (e.g. panel expanded); defaults to true
 * @returns {{ revisions: DocumentRevision[]; isLoading: boolean; error: Error | null }} The revisions, loading state, and error
 */
const useDocumentRevisions = (
  documentId: string | undefined,
  enabled = true,
): { revisions: DocumentRevision[]; isLoading: boolean; error: Error | null } => {
  const query = useQuery({
    queryKey: ['document-revisions', documentId],
    queryFn: () => {
      if (!documentId) throw new Error('Document ID required');
      return getDocumentRevisions(documentId);
    },
    enabled: !!documentId && enabled,
  });

  return {
    revisions: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

/**
 * Hook to fetch Flora user preferences for the current organization
 * @returns {UseFloraUserPreferencesResult} Object containing preferences, loading state, and error
 */
const useFloraUserPreferences = (): UseFloraUserPreferencesResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: ['flora-user-preferences', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getFloraUserPreferences(organizationId);
    },
    enabled: !!organizationId,
  });

  return {
    preferences: query.data?.preferences,
    isLoading: query.isLoading,
    error: query.error,
  };
};

/**
 * Hook to fetch the Flora capability catalog for the current organization.
 * The catalog is already merged with the user's saved Customize Flora settings.
 *
 * @returns {UseFloraCapabilitiesResult} Object containing the catalog, loading state, and error
 */
const useFloraCapabilities = (): UseFloraCapabilitiesResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: ['flora-capabilities', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getFloraCapabilities(organizationId);
    },
    enabled: !!organizationId,
  });

  return {
    capabilities: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
};

/**
 * Mutation key shared by every Flora preferences save, so callers can tell
 * whether other saves are still in flight.
 */
const FLORA_USER_PREFERENCES_MUTATION_KEY = ['flora-user-preferences', 'update'] as const;

/**
 * Hook to update Flora user preferences.
 *
 * Saves run one at a time (a shared mutation `scope`), so quick successive
 * autosaves reach the server in the order they were made. The preferences
 * query is refetched only after the last queued save settles, so a refetch
 * can't briefly undo an optimistic update that is still waiting its turn.
 *
 * @returns {UseMutationResult<FloraUserPreferencesResponse, Error, FloraUserPreferences, unknown>} React Query mutation result for updating Flora user preferences
 */
const useUpdateFloraUserPreferences = (): UseMutationResult<
  FloraUserPreferencesResponse,
  Error,
  FloraUserPreferences,
  unknown
> => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: FLORA_USER_PREFERENCES_MUTATION_KEY,
    scope: { id: 'flora-user-preferences' },
    mutationFn: (data: FloraUserPreferences) => {
      if (!organizationId) throw new Error('Organization ID required');
      return updateFloraUserPreferences(organizationId, data);
    },
    onSettled: () => {
      // `isMutating` still counts this mutation while its callbacks run.
      if (queryClient.isMutating({ mutationKey: FLORA_USER_PREFERENCES_MUTATION_KEY }) <= 1) {
        queryClient.invalidateQueries({ queryKey: ['flora-user-preferences'] });
      }
    },
  });
};

/**
 * Hook to fetch the current user's saved long-term memories
 * @returns {UseFloraMemoriesResult} Object containing memories, loading state, and error
 */
const useFloraMemories = (): UseFloraMemoriesResult => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: ['flora-memories', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getFloraMemories(organizationId);
    },
    enabled: !!organizationId,
  });

  return {
    memories: query.data?.memories ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

/**
 * Hook to delete a long-term memory
 * @returns {UseMutationResult<void, Error, string, unknown>} React Query mutation result
 */
const useDeleteFloraMemory = (): UseMutationResult<void, Error, string, unknown> => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (!organizationId) throw new Error('Organization ID required');
      return deleteFloraMemory(organizationId, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flora-memories'] });
    },
  });
};

/**
 * Hook to fetch all visible slash commands for the current organization
 * @returns {{ commands: SlashCommand[]; isLoading: boolean; error: Error | null }} Object containing commands, loading state, and error
 */
const useSlashCommands = (): { commands: SlashCommand[]; isLoading: boolean; error: Error | null } => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: ['slash-commands', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getSlashCommands(organizationId);
    },
    enabled: !!organizationId,
  });

  return {
    commands: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

/**
 * Hook to create a new slash command
 * @returns {UseMutationResult<SlashCommand, Error, SlashCommandWrite, unknown>} React Query mutation result for creating a slash command
 */
const useCreateSlashCommand = (): UseMutationResult<SlashCommand, Error, SlashCommandWrite, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SlashCommandWrite) => createSlashCommand(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slash-commands'] });
    },
  });
};

/**
 * Hook to update an existing slash command
 * @returns {UseMutationResult<SlashCommand, Error, { id: string; data: Partial<SlashCommandWrite> }, unknown>} React Query mutation result for updating a slash command
 */
const useUpdateSlashCommand = (): UseMutationResult<
  SlashCommand,
  Error,
  { id: string; data: Partial<SlashCommandWrite> },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SlashCommandWrite> }) => updateSlashCommand(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slash-commands'] });
    },
  });
};

/**
 * Hook to delete a slash command
 * @returns {UseMutationResult<void, Error, string, unknown>} React Query mutation result for deleting a slash command
 */
const useDeleteSlashCommand = (): UseMutationResult<void, Error, string, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSlashCommand(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slash-commands'] });
    },
  });
};

export {
  ORGANIZATION_DOCUMENTS_QUERY_KEY,
  useCreateSlashCommand,
  useDeleteFloraMemory,
  useDeleteSlashCommand,
  useDocumentContent,
  useDocumentPreview,
  useDocumentRevisions,
  useFloraCapabilities,
  useFloraMemories,
  useFloraUserPreferences,
  useOrganizationDocuments,
  useSlashCommands,
  useSuggestedPrompts,
  useUpdateFloraUserPreferences,
  useUpdateSlashCommand,
};
